/**
 * Custom hook for SupplierDetail state management
 */

import { useEffect, useState, useMemo } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
  suppliersAPI,
  SupplierProfile,
  purchasingAPI,
  PurchaseOrder,
  qualityAPI,
  NonConformanceReport,
  coreAPI,
  UserProfile,
  Company,
} from '../../lib/api';
import { useAuth } from '../../contexts/AuthContext';
import { useView } from '../../contexts/ViewContext';
import { useIsCustomer, usePortalMode } from '../../hooks/usePortalMode';
import { SupplierDetailHook } from './types';
import { AxiosError } from 'axios';
import { getApiErrorCode, getApiErrorMessage } from '../../utils/errorUtils';
import { usersListedForViewer } from './utils';
import { useSupplierUserActions } from './useSupplierUserActions';

/** Orders per page on the supplier's Orders tab. */
export const ORDERS_PAGE_SIZE = 25;

export function useSupplierDetail(): SupplierDetailHook {
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'overview';

  // Supplier data state
  const [supplier, setSupplier] = useState<SupplierProfile | null>(null);
  // CR-4815 r6: the embedded supplier.company_details is the slim
  // CompanyNestedSerializer (CWE-359 PII strip) and omits registration_number,
  // primary_email/phone, website and the address_line_* fields the Overview
  // "Company Information" card renders. Fetch the full Company separately so the
  // card reflects what's actually persisted (and refreshes after an edit).
  const [companyInfo, setCompanyInfo] = useState<Company | null>(null);
  const [orders, setOrders] = useState<PurchaseOrder[]>([]);
  // Orders tab paginates server-side; `ordersCount` is DRF's unpaginated
  // total so the tab can show a real "x–y of N" rather than guessing.
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersCount, setOrdersCount] = useState(0);
  // CR-7028: Supplier code filter for orders
  const [ordersSupplierCode, setOrdersSupplierCode] = useState('');
  const [ncrs, setNCRs] = useState<NonConformanceReport[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);

  // Loading states
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [ncrsLoading, setNCRsLoading] = useState(false);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersAccessCode, setUsersAccessCode] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Active tab (honours ?tab=<name> on initial load)
  const [activeTab, setActiveTab] = useState(initialTab);

  const [userSearch, setUserSearch] = useState('');


  // Computed values
  const { isHelix: isHelixAdmin } = usePortalMode();
  const isCustomer = useIsCustomer();
  const { user } = useAuth();
  // CR-5857: customer admins (or helix admins) gain Edit + Reset Password.
  // Mirrors UsersManagement's canManageUsers predicate.
  const isCustomerAdmin =
    isHelixAdmin ||
    (isCustomer && Boolean(user?.is_company_admin || user?.can_manage_users));
  // CR-5977 follow-up: pass view scope to the profile endpoint so the
  // detail header's OTD / PPM / NCR aggregates narrow to the same customer
  // set that the OTD drilldown applies — mirrors useSuppliers.ts' pattern
  // for the list page.
  const { viewLevel, getViewQueryParams } = useView();
  const isOwnCompany = !!(supplier && user && user.company === supplier.company);

  // Filter users by search
  const filteredUsers = useMemo(() => {
    return users.filter((u) => {
      if (!userSearch) return true;
      const search = userSearch.toLowerCase();
      return (
        u.user.full_name?.toLowerCase().includes(search) ||
        u.user.email.toLowerCase().includes(search) ||
        u.job_title?.toLowerCase().includes(search) ||
        u.role_name?.toLowerCase().includes(search)
      );
    });
  }, [users, userSearch]);

  // Fetch supplier data on mount and whenever the active view changes,
  // so the header KPIs re-narrow when the user switches group/site context.
  const viewParams = getViewQueryParams();
  const viewParamsKey = JSON.stringify({ viewLevel, ...viewParams });
  useEffect(() => {
    if (id) {
      fetchSupplierData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isCustomer, viewParamsKey]);

  // Lazy load tab data
  useEffect(() => {
    if (activeTab === 'orders' && orders.length === 0 && supplier) {
      fetchOrders();
    } else if (activeTab === 'quality' && ncrs.length === 0 && supplier) {
      fetchNCRs();
    } else if (activeTab === 'users' && users.length === 0 && supplier) {
      fetchUsers();
    }
  }, [activeTab, supplier]);

  const fetchSupplierData = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await suppliersAPI.getProfile(Number(id), {
        view_level: viewLevel,
        ...getViewQueryParams(),
      });
      setSupplier(response.data);
      // CR-4815 r6: pull the full Company so the Overview card can render the
      // fields the slim company_details embed strips. Best-effort.
      void fetchCompanyInfo(response.data.company);
    } catch (err: unknown) {
      console.error('Error fetching supplier:', err);
      setError(getApiErrorMessage(err, 'Failed to load supplier details'));
    } finally {
      setLoading(false);
    }
  };

  // CR-9077: re-read the profile without the page spinner, so the header's
  // strategic star follows the Strategic Supplier toggle at once.
  const refreshSupplier = async () => {
    try {
      const response = await suppliersAPI.getProfile(Number(id), {
        view_level: viewLevel,
        ...getViewQueryParams(),
      });
      setSupplier(response.data);
    } catch (err: unknown) {
      console.error('Error refreshing supplier:', err);
    }
  };

  const fetchCompanyInfo = async (companyId: number) => {
    // CR-4815 r6: read-only users without company-detail access get a 403
    // here — fall back to the slim company_details on the supplier profile
    // rather than surfacing an error (the card just shows fewer fields).
    try {
      const response = await coreAPI.getCompany(companyId);
      setCompanyInfo(response.data);
    } catch (err: unknown) {
      console.info('[CR-4815] full company fetch unavailable — using slim embed', err);
      setCompanyInfo(null);
    }
  };

  /**
   * Fetch one page of this supplier's orders.
   *
   * Previously capped at page_size=10 with no way forward: the tab showed the
   * ten most recent orders and a "View All" button that bounced to Purchasing.
   * A supplier with hundreds of POs was therefore unreadable here, and there
   * was no way to tell ten-of-ten from ten-of-four-hundred.
   *
   * @param page - 1-based page to load. Defaults to the current page.
   * @param supplierCode - CR-7028: Optional supplier code filter.
   */
  const fetchOrders = async (page: number = ordersPage, supplierCode?: string) => {
    if (!supplier) return;

    // CR-7028: Use provided supplierCode or fall back to current state
    const effectiveSupplierCode = supplierCode !== undefined ? supplierCode : ordersSupplierCode;

    try {
      setOrdersLoading(true);
      const params: Record<string, string | number> = {
        supplier: supplier.company,
        ordering: '-created_at',
        page,
        page_size: ORDERS_PAGE_SIZE,
      };
      // CR-7028: Add supplier code filter if provided
      if (effectiveSupplierCode) {
        params['supplier__supplier_code__icontains'] = effectiveSupplierCode;
      }
      const response = await purchasingAPI.getOrders(params);
      setOrders(response.data.results);
      // DRF returns the unpaginated total in `count` — that is what makes an
      // honest "x–y of N" possible instead of inferring from the page length.
      setOrdersCount(response.data.count ?? response.data.results.length);
      setOrdersPage(page);
    } catch (err: unknown) {
      console.error('Error fetching orders:', err);
    } finally {
      setOrdersLoading(false);
    }
  };

  const fetchNCRs = async () => {
    if (!supplier) return;

    try {
      setNCRsLoading(true);
      const response = await qualityAPI.getNCRs({
        supplier: supplier.company,
        ordering: '-created_at',
        page_size: 10,
      });
      setNCRs(response.data.results);
    } catch (err: unknown) {
      console.error('Error fetching NCRs:', err);
    } finally {
      setNCRsLoading(false);
    }
  };

  const fetchUsers = async () => {
    if (!supplier) return;

    try {
      setUsersLoading(true);
      setUsersAccessCode(null);
      // CR-7255 review: ask first — a refusal is not an empty list. Keeping
      // why lets the tab explain it and drop Add User, and no 403 is logged.
      const access = await coreAPI.getCompanyUsersAccess(supplier.company);
      if (!access.data.can_view) {
        setUsers([]);
        setUsersAccessCode(access.data.code ?? 'forbidden');
        return;
      }
      const response = await coreAPI.getCompanyUsers(supplier.company);
      setUsers(usersListedForViewer(response.data, supplier.company, isCustomer && !isHelixAdmin));
      setUsersAccessCode(null);
    } catch (err: unknown) {
      if (err instanceof AxiosError && err.response?.status === 403) {
        setUsersAccessCode(getApiErrorCode(err) ?? 'forbidden');
      } else {
        console.error('Error fetching users:', err);
      }
    } finally {
      setUsersLoading(false);
    }
  };

  const handleUpdateProfile = async (data: Partial<SupplierProfile>) => {
    if (!supplier) return;
    const response = await suppliersAPI.updateProfile(supplier.company, data);
    setSupplier(response.data);
  };

  const handleUpdateCompany = async (data: Record<string, string | number | boolean | null>) => {
    if (!supplier) return;
    // CR-4815: send only the delta. Spreading supplier.company_details included
    // read-only/computed fields that the backend rejected, which caused the
    // Company PATCH to fail AFTER the SupplierProfile PATCH had already saved —
    // producing a partial update + misleading error toast.
    //
    // CR-4815 round 4: switched coreAPI.updateCompany (PUT) → coreAPI.patchCompany
    // (PATCH). The earlier rounds delivered a delta payload to a PUT endpoint,
    // and DRF's full-replace semantics rejected the request — Sarah's MAB-staging
    // bounce surfaced as a 403 on PUT /api/v1/core/companies/<id>/. PATCH is the
    // correct verb for partial updates; the backend's CompanyViewSet allows
    // PATCH from any company admin (CR-4819 wiring), so the delta now lands.
    await coreAPI.patchCompany(supplier.company, data as Partial<Company>);
    // Refresh supplier data to get updated company_details, and re-pull the
    // full Company so the Overview card reflects the saved values (CR-4815 r6:
    // the slim company_details embed never carries them).
    await fetchSupplierData();
    await fetchCompanyInfo(supplier.company);
  };

  const handleUploadLogo = async (file: File) => {
    if (!supplier) return;
    await coreAPI.uploadCompanyLogo(supplier.company, file);
    await fetchSupplierData();
  };

  // Users-tab pop-up, permissions, edit/reset password and Login As.
  const userActions = useSupplierUserActions({
    supplier,
    fetchUsers,
    canImpersonate: isHelixAdmin || isCustomer,
  });

  return {
    ...userActions,
    // State
    supplier,
    companyInfo,
    orders,
    ordersPage,
    ordersCount,
    ordersPageSize: ORDERS_PAGE_SIZE,
    ordersSupplierCode,
    ncrs,
    users,
    loading,
    ordersLoading,
    ncrsLoading,
    usersLoading,
    usersAccessCode,
    error,
    activeTab,
    userSearch,

    // Actions
    fetchSupplierData,
    refreshSupplier,
    fetchOrders,
    fetchNCRs,
    fetchUsers,
    setActiveTab,
    setUserSearch,
    handleUpdateProfile,
    handleUpdateCompany,
    handleUploadLogo,
    // CR-7028: Orders filter
    setOrdersSupplierCode,

    // Computed
    filteredUsers,
    isHelixAdmin,
    isCustomer,
    isCustomerAdmin,
    isOwnCompany,
  };
}
